import React, { useEffect, useState } from 'react'
import SwipeableViews from 'react-swipeable-views'
import axios from 'axios'
import classNames from 'classnames'
import Collapse from '@mui/material/Collapse'
import map from 'lodash/map'
import some from 'lodash/some'
import includes from 'lodash/includes'
import split from 'lodash/split'
import moment from 'moment-timezone'
import Tooltip from '@mui/material/Tooltip'
import SolidsView from './SolidsView'
import LiquidsView from './LiquidsView'
import CookingUnitsView from './CookingUnitsView'
import PlatesView from './PlatesView'
import OilView from './OilView'
import ProductContainerModal from './ProductContainerModal'
import PlateContainerModal from './PlateContainerModal'
import FlashNotification from '../shared/flashNotification'
import { LocalizationContext } from '../shared/contexts'
import { t } from '../../i18n'
import Icon from '../shared/icon'
import { ReplenishmentOrder, ReplenishmentOrderAction } from './types'
import { ReplenishmentCartSidebar } from './replenishment/CartSidebar'
import { ReplenishmentOrderPlacedModal } from './replenishment/OrderPlacedModal'
import { ProductReplenishmentModal } from './replenishment/ProductReplenishmentModal'
import { BowlReplenishmentModal } from './replenishment/BowlReplenishmentModal'
import { OilReplenishmentModal } from './replenishment/OilReplenishmentModal'
import { ReplenishmentHistorySidebar } from './replenishment/HistorySidebar'
import consumer from '../../../src/cable'

const scope = 'stock_control'

// Built lazily inside the component so the "tomorrow" default uses the
// tenant's timezone, not the browser's, and reflects the current moment when
// the user actually lands on the page (not module load time).
const buildEmptyCart = (tenantTz: string): ReplenishmentOrder => ({
  productReplenishments: [],
  plateReplenishments: [],
  oilReplenishment: null,
  state: 'draft',
  deliveryDate: moment.tz(tenantTz).add(1, 'days'),
  editableOrderId: null,
  createdAt: null
} as ReplenishmentOrder)

const Tab = ({ label, index, currentTabIndex, hasAlert, switchTab }) => {
  const classes = classNames(
    'tab', {
      'active': index === currentTabIndex,
      'alert-indicator': hasAlert
    }
  )

  return (
    <div className={classes} onClick={() => switchTab(index)}>
      {label}
    </div>
  )
}

const Tabs = ({ currentTabIndex, switchTab, alerts }) => {
  const tabs = [
    { name: 'solids', label: t('tabs.solid_products', { scope }) },
    { name: 'liquids', label: t('tabs.liquid_products', { scope }) },
    // { name: 'cooking_units', label: t('tabs.cooking_units', { scope }) }, // TODO: Uncomment for batch recipes
    { name: 'plates', label: t('tabs.bowls', { scope }) },
    { name: 'oil', label: t('tabs.oil', { scope }) }
  ]

  return (
    <div className='tabs-wrapper flex-1'>
      {map(tabs, ({ name, label }, index) =>
        <Tab key={name}
          label={label}
          index={index}
          currentTabIndex={currentTabIndex}
          switchTab={switchTab}
          hasAlert={alerts[name]}
        />
      )}
    </div>
  )
}

const Search = ({ query, onChange }) => {
  return (
    <div className='search-wrapper form-group'>
      <input
        className='search-input form-control'
        value={query}
        onChange={onChange}
        placeholder='search ...'
      />
    </div>
  )
}

const CategoryWithChildren = ({
  id, name, ancestry, children, currentProductCategoryAncestry, setCurrentProductCategoryAncestry
}) => {
  const parseAncestry = (categoryAncestry) => {
    if (!categoryAncestry) return []

    const separator = '/'
    return map(split(categoryAncestry, separator), idStr => parseInt(idStr))
  }

  const openedAncestryIds = parseAncestry(currentProductCategoryAncestry)
  const isExpanded = includes(openedAncestryIds, id)
  const isSelected = id === openedAncestryIds[openedAncestryIds.length - 1]

  let targetAncestryIds = parseAncestry(ancestry)

  if (!isExpanded || (ancestry == null && !isSelected)) {
    targetAncestryIds.push(id)
  }

  return (
    <li className='category'>
      <div
        className={`category-link pointer ${isSelected ? 'selected' : ''}`}
        onClick={() => setCurrentProductCategoryAncestry(targetAncestryIds)}
      >
        <span className='category-name'>{name}</span>
        {children.length > 0 &&
          <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
        }
      </div>
      <Collapse in={isExpanded}>
        <ul>
          {map(children, child =>
            <CategoryWithChildren
              {...child}
              key={child.id}
              currentProductCategoryAncestry={currentProductCategoryAncestry}
              setCurrentProductCategoryAncestry={setCurrentProductCategoryAncestry}
            />
          )}
        </ul>
      </Collapse>
    </li>
  )
}

const StockControl = ({ localization_context, current_location }) => {
  const tenantTz = localization_context?.timezone || 'UTC'
  const [currentTabIndex, setCurrentTabIndex] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(
    localStorage.getItem('stocks_control_expanded_sidebar') !== 'false'
  );
  const [solidContainers, setSolidContainers] = useState({})
  const [liquidContainers, setLiquidContainers] = useState({})
  const [plateContainers, setPlateContainers] = useState({})
  const [cookingUnits, setCookingUnits] = useState({})
  const [oilLevel, setOilLevel] = useState<{oil_remaining: number, oil_capacity: number, oil_status: string }>({
    oil_remaining: 0,
    oil_capacity: 0,
    oil_status: 'full'
  })
  const [expandedContainerSlot, setExpandedContainerSlot] = useState(null)
  const [expandedPlateContainerSlot, setExpandedPlateContainerSlot] = useState(null)
  const [oilContainerExpanded, setOilContainerExpanded] = useState(false)
  const [productCategories, setProductCategories] = useState([])
  const [solidProductCategories, setSolidProductCategories] = useState([])
  const [liquidProductCategories, setLiquidProductCategories] = useState([])
  const [currentProductCategory, setCurrentProductCategory] = useState({
    ancestry: null,
    id: null
  })
  const [flash, setFlash] = useState({
    flashOpen: false,
    flashType: null,
    flashMessage: null
  })

  const [replenishmentView, setReplenishmentView] = useState(!!new URLSearchParams(window.location.search).get('replenishment'))
  const [replenishmentSidebarOpen, setReplenishmentSidebarOpen] = useState(!!new URLSearchParams(window.location.search).get('open-sidebar'))
  const [showOrderPlacedModal, setShowOrderPlacedModal] = useState({ show: false, orderState: null })
  const [draftReplenishmentOrder, setDraftReplenishmentOrder] = useState(buildEmptyCart(tenantTz))
  const [placedReplenishmentOrder, setPlacedReplenishmentOrder] = useState(null)
  const [activeReplenishmentOrders, setActiveReplenishmentOrders] = useState([])
  const [replenishmentErrorMessages, setReplenishmentErrorMessages] = useState({})
  const [replenishmentHistorySidebarOpen, setReplenishmentHistorySidebarOpen] = useState(false)
  const [kitchenConfiguration, setKitchenConfiguration] = useState<any>({})

  useEffect(() => {
    loadProductCategories()
    refreshStocks()
    fetchDraftReplenishmentOrder()
    fetchActiveReplenishmentOrders()
    fetchKitchenConfiguration()

    consumer.subscriptions.create({
      channel: 'ReplenishmentStateChannel'
    }, {
      received: fetchDraftReplenishmentOrder
    })
  }, [])


  useEffect(() => {
    document.body.style.backgroundColor = replenishmentView ? "#F2F2F2" : "#FFFFFF"
  }, [replenishmentView])

  const setFlashMessage = (flashType, flashMessage) => (
    setFlash({flashType: flashType, flashMessage: flashMessage, flashOpen: true})
  )

  useEffect(() => {
    if(flash.flashOpen) {
      setTimeout(() => {
        setFlash({flashType: null, flashMessage: null, flashOpen: false})
      }, 3000)
    }
  }, [flash])

  const fetchKitchenConfiguration = () => {
    axios.get('/api/internal/locations/current_kitchen_configuration').then(({ data }) => {
      setKitchenConfiguration(data)
    }).catch(() => setFlashMessage('danger', 'Failed to load kitchen configuration'))
  }

  const refreshStocks = async () => {
    await refreshSolidsStock()
    refreshLiquidsStock()
    refreshPlatesStock()
    refreshCookingUnits()
    refreshOilLevel()
  }

  const loadProductCategories = () => {
    axios.get('/stocks/product_categories').then(({ data }) => {
      setProductCategories(data.product_categories)
      setSolidProductCategories(data.solid_product_categories)
      setLiquidProductCategories(data.liquid_product_categories)
    }).catch(() => setFlashMessage('danger', 'Failed to load product categories'))
  }

  const refreshSolidsStock = () => {
    return axios.get('/stocks/active_solid_containers').then(({ data }) => {
      setSolidContainers(data)
    }).catch(() => setFlashMessage('danger', 'Failed to load product containers'))
  }

  const refreshLiquidsStock = () => {
    axios.get('/stocks/active_liquid_containers').then(({ data }) => {
      setLiquidContainers(data)
    }).catch(() => setFlashMessage('danger', 'Failed to load sauce containers'))
  }

  const refreshPlatesStock = () => {
    axios.get('/stocks/active_plate_containers').then(({ data }) => {
      setPlateContainers(data)
    }).catch(() => setFlashMessage('danger', 'Failed to load plate containers'))
  }

  const refreshCookingUnits = () => {
    axios.get('/stocks/cooking_units.json').then(({ data }) => {
      setCookingUnits(data.sort((a, b) => a.slot_number - b.slot_number))
    }).catch(() => setFlashMessage('danger', 'Failed to load cooking units'))
  }

  const refreshOilLevel = () => {
    axios.get('/stocks/oil_level').then(({ data }) => {
      setOilLevel(data)
    }).catch(() => setFlashMessage('danger', 'Failed to load oil level'))
  }

  const fetchDraftReplenishmentOrder = () => {
    axios.get('/replenishment_orders/cart?format=json').then(({data}) => {
      setDraftReplenishmentOrder({...data, deliveryDate: data.deliveryDate || moment.tz(tenantTz).add(1, 'days').format()})
    })
  }

  const fetchActiveReplenishmentOrders = () => {
    axios.get(`/replenishment_orders/active?format=json`).then(({ data }) => {
      setActiveReplenishmentOrders(data.replenishment_orders)
    })
  }

  const plateReplenishmentsData = (plateReplenishments) => {
    return map(plateReplenishments, replenishment => (
      {
      id: replenishment.id,
      slot_number: replenishment.slotNumber,
      amount: replenishment.amount,
      _destroy: replenishment.destroy
    }))
  }

  const getCurrentProductCategories = () => {
    switch(currentTabIndex) {
      case 0:
        return solidProductCategories
      case 1:
        return liquidProductCategories
      default:
        return productCategories
    }
  }

  const productReplenishmentsData = (productReplenishments) => {
    return map(productReplenishments, replenishment => (
      {
        id: replenishment.id,
        slot_number: replenishment.slotNumber,
        product_id: replenishment.product?.id || null,
        product_container_id: replenishment.productContainer?.id,
        container_type: replenishment.containerType,
        _destroy: replenishment.destroy
      }
    ))
  }

  // TODO: Refactor draft replenishment order update handling and endpoints:

  const updateCartRequest = (data, action) => {
    switch(action) {
      case ReplenishmentOrderAction.UPDATE_SENT_ORDER:
        return axios.put(`/replenishment_orders/${data.id}/merge_draft_to_sent_order?format=json`, data)
      case ReplenishmentOrderAction.PLACE_ORDER:
        return axios.put(`/replenishment_orders/${data.id}/place_order?format=json`, data)
      case ReplenishmentOrderAction.CANCEL:
        return axios.delete(`/replenishment_orders/${data.id}?format=json`)
      default:
        return data.id
          ? axios.put(`/replenishment_orders/${draftReplenishmentOrder.id}?format=json`, data) // cart already stored, updating it
          : axios.post('/replenishment_orders?format=json', data) // first change of draft cart, creating it
    }
  }

  // Resolves with the message to show when the server rejected the cart, or
  // null when it saved. Callers that report an outcome have to wait for this
  // rather than assume the request succeeded.
  const updateReplenishmentCart = (
    updatedReplenishmentOrder: ReplenishmentOrder, action: ReplenishmentOrderAction
  ): Promise<string | null> => {
    if(action == ReplenishmentOrderAction.CHANGE_DATE &&
      (!updatedReplenishmentOrder.deliveryDate?.isValid() || updatedReplenishmentOrder.deliveryDate == null)) {
      return Promise.resolve(null)
    }
    const data = {
      id: updatedReplenishmentOrder.id,
      delivery_date: updatedReplenishmentOrder.deliveryDate,
      plate_replenishments_attributes: plateReplenishmentsData(updatedReplenishmentOrder.plateReplenishments),
      product_replenishments_attributes: productReplenishmentsData(updatedReplenishmentOrder.productReplenishments),
      oil_replenishment_attributes: updatedReplenishmentOrder.oilReplenishment ? {
        id: updatedReplenishmentOrder.oilReplenishment.id,
        amount: updatedReplenishmentOrder.oilReplenishment.amount,
        _destroy: updatedReplenishmentOrder.oilReplenishment.destroy
      } : null,
    }

    return updateCartRequest(data, action).then(({data}) => {
      handleOrderUpdated(data, action)
      return null
    }).catch(({ response }) => {
      const fullErrors: Record<string, string[]> = response?.data?.full_errors || {}
      setReplenishmentErrorMessages(fullErrors)

      return Object.values(fullErrors).flat().join(', ') || t('title', { scope: 'error_modal' })
    })
  }

  const handleOrderUpdated = (data, action) => {
    setReplenishmentErrorMessages({})
    if(action == ReplenishmentOrderAction.CANCEL) {
      if(draftReplenishmentOrder.editableOrderId) {
        fetchDraftReplenishmentOrder()
      } else {
        setDraftReplenishmentOrder(buildEmptyCart(tenantTz))
      }
      return
    }
    fetchDraftReplenishmentOrder()
    if(action == ReplenishmentOrderAction.PLACE_ORDER) {
      onReplenishmentOrderPlaced(data)
    } else if(action == ReplenishmentOrderAction.UPDATE_SENT_ORDER) {
      onSentReplenishmentOrderUpdated()
    }
  }

  const onReplenishmentOrderPlaced = (data) => {
    setPlacedReplenishmentOrder(data)
    fetchActiveReplenishmentOrders()
    setReplenishmentSidebarOpen(false)
    setShowOrderPlacedModal({ show: true, orderState: 'placed' })
  }

  const onSentReplenishmentOrderUpdated = () => {
    setReplenishmentSidebarOpen(false)
    setShowOrderPlacedModal({ show: true, orderState: 'updated' })
  }

  const switchTab = (index) => {
    setCurrentTabIndex(index)
  }

  const tabAlerts = () => {
    const hasAlert = (containers) => some(containers, container => container.show_alert)

    return {
      solids: hasAlert(solidContainers),
      liquids: hasAlert(liquidContainers),
      cooking_units: hasAlert(cookingUnits),
      plates: hasAlert(plateContainers),
      oil: oilLevel.oil_status == 'low'
    }
  }

  const handleSearch = ({ target: { value } }) => {
    setSearchQuery(value)
    setCurrentProductCategory({ancestry: null, id: null})
  }

  useEffect(() => {
    localStorage.setItem('stocks_control_expanded_sidebar', isSidebarExpanded.toString())
    if(!isSidebarExpanded) {
      setCurrentProductCategory({ancestry: null, id: null})
    }
  }, [isSidebarExpanded])

  const toggleSidebar = () => {
    setIsSidebarExpanded(!isSidebarExpanded);
  }

  const expandContainer = (slotNumber, isLiquid) => {
    setExpandedContainerSlot({number: slotNumber, isLiquid})
  }

  const expandPlateContainer = (slotNumber) => {
    setExpandedPlateContainerSlot(slotNumber)
  }

  const expandOilContainer = () => {
    setOilContainerExpanded(true)
  }

  const closeContainer = () => {
    setExpandedContainerSlot(null)
  }

  const closePlateContainer = () => {
    setExpandedPlateContainerSlot(null)
  }

  const setCurrentProductCategoryAncestry = (currentProductCategoryAncestryIds) => {
    setCurrentProductCategory({
      ancestry: currentProductCategoryAncestryIds.join('/'),
      id: currentProductCategoryAncestryIds[currentProductCategoryAncestryIds.length - 1]
    })
  }

  const renderFlashNotification = () => flash.flashOpen && (
    <FlashNotification
      type={flash.flashType}
      message={flash.flashMessage}
      onClose={() => setFlash({...flash, flashOpen: false})}
    />
  )

  const toggleReplenishmentView = (replenishmentViewOn: boolean) => {
    setReplenishmentView(replenishmentViewOn)
  }

  const toggleReplenishmentSidebar = (toggle: boolean) => {
    setReplenishmentErrorMessages({})
    setReplenishmentSidebarOpen(toggle)
    if(!toggle) {
      setReplenishmentHistorySidebarOpen(false)
    }
  }

  const enterReplenishmentEditView = () => {
    axios.post('/replenishment_orders/enter_edit_order?format=json').then((response) => {
      setDraftReplenishmentOrder(response.data)
    })
  }

  const getOrderState = () => {
    const { state, editableOrderId } = draftReplenishmentOrder

    if (state == 'draft') {
      return editableOrderId != null ? 'editing' : 'draft'
    } else if (state == 'closed') {
      return 'closed'
    } else {
      return 'sent'
    }
  }

  const orderState = getOrderState()

  const orderStatusClassName = () => {
    switch(orderState) {
      case 'draft': return 'green'
      case 'editing': return 'yellow'
      case 'sent': return 'green'
      case 'closed': return 'green'
    }
  }

  const currentDate = moment.tz(tenantTz).startOf('day')

  const currentProductCategories = getCurrentProductCategories()

  return (
    <div className={`stock-control ${replenishmentView ? 'replenishment' : ''}`}>
      <LocalizationContext.Provider value={localization_context}>
        <div className='stock-control-header'>
          <div className='section-heading ps-3 pt-2'>
            <div className='section-heading-row bold'>{t(replenishmentView ? 'replenishment' : 'stock_control', { scope })}</div>
          </div>
          <Tabs currentTabIndex={currentTabIndex} switchTab={switchTab} alerts={tabAlerts()} />
          <Search query={searchQuery} onChange={handleSearch} />
          <div className='replenishment-actions'>
            {!replenishmentView &&
              <button className='btn btn-primary replenishment-btn' onClick={() => {toggleReplenishmentView(true)}}>{t('replenishment', { scope })}</button>
            }
            {replenishmentView &&
              <>
                <Tooltip
                  title={'Order history'}
                  placement='top'
                  arrow
                >
                  <div className='replenishment-icon-container me-1'>
                    <Icon className='pointer' name='history' onClick={() => setReplenishmentHistorySidebarOpen(true)}/>
                    {activeReplenishmentOrders.length > 0 &&
                      <div className='status-indicator green'></div>
                    }
                  </div>
                </Tooltip>
                {(orderState == 'draft' || orderState == 'editing') &&
                  <Tooltip
                    title={'Cart'}
                    placement='top'
                    arrow
                  >
                    <div className='replenishment-icon-container me-1'>
                      <Icon className='pointer' name='basket' onClick={() => setReplenishmentSidebarOpen(true)}/>
                      {draftReplenishmentOrder.productReplenishments.length > 0 &&
                        <div className={`status-indicator ${orderStatusClassName()}`}></div>
                      }
                    </div>
                  </Tooltip>
                }
                <Icon name='close' className="last" onClick={() => {toggleReplenishmentView(false)}}/>
              </>
            }
          </div>
        </div>
        <div className='stock-control-body' style={{display: currentTabIndex == 2 ? 'inline-block' : 'inline-flex'}}>
          {[0,1].includes(currentTabIndex) &&
                    <div className={`categories-sidebar ${!isSidebarExpanded ? 'collapsed' : ''}`}>
                    <Collapse in={isSidebarExpanded} orientation='horizontal'>
                      <ul className='categories-list'>
                        {map(currentProductCategories, productCategory =>
                          <CategoryWithChildren
                            { ...productCategory }
                            key={productCategory.id}
                            currentProductCategoryAncestry={currentProductCategory.ancestry}
                            setCurrentProductCategoryAncestry={setCurrentProductCategoryAncestry}
                          />
                        )}
                      </ul>
                    </Collapse>
                    <div className='collapse-toggle' onClick={toggleSidebar}>
                      <i className={`fa ${isSidebarExpanded ? 'fa-angle-left' : 'fa-angle-right'}`} />
                    </div>
                  </div>
          }
          <div className='tab-content-wrapper'>
            <SwipeableViews index={currentTabIndex} style={{width: 'unset'}}>
              <SolidsView
                searchQuery={searchQuery}
                currentProductCategoryId={currentProductCategory.id}
                currentDate={currentDate}
                containers={solidContainers}
                expandContainer={expandContainer}
                draftReplenishmentOrder={draftReplenishmentOrder}
                activeReplenishmentOrders={activeReplenishmentOrders}
                replenishmentView={replenishmentView}
                disabledSlotNumbers={
                  kitchenConfiguration.fixed_container_slots
                    ?.filter(slot => slot.state == 'disabled').map(slot => slot.slot_number)
                }
                faultySlotNumbers={
                  kitchenConfiguration.fixed_container_slots
                    ?.filter(slot => slot.is_faulty).map(slot => slot.slot_number)
                }
              />
              <LiquidsView
                searchQuery={searchQuery}
                currentProductCategoryId={currentProductCategory.id}
                currentDate={currentDate}
                containers={liquidContainers}
                expandContainer={expandContainer}
                draftReplenishmentOrder={draftReplenishmentOrder}
                activeReplenishmentOrders={activeReplenishmentOrders}
                replenishmentView={replenishmentView}
                disabledSlotNumbers={
                  kitchenConfiguration.mobile_container_slots
                    ?.filter(slot => slot.state == 'disabled').map(slot => slot.slot_number)
                }
                faultySlotNumbers={
                  kitchenConfiguration.mobile_container_slots
                    ?.filter(slot => slot.is_faulty).map(slot => slot.slot_number)
                }
              />
              {/* <CookingUnitsView
                cookingUnits={cookingUnits}
                disabledSlotNumbers={
                  kitchenConfiguration.cooking_units
                    ?.filter(slot => slot.state == 'disabled').map(slot => slot.slot_number)
                }
              /> */}
              <PlatesView
                plateContainers={plateContainers}
                expandPlateContainer={expandPlateContainer}
                replenishmentView={replenishmentView}
                draftReplenishmentOrder={draftReplenishmentOrder}
                isSelectedTab={currentTabIndex == 2}
                disabledSlotNumbers={
                  kitchenConfiguration.plate_stands
                    ?.filter(slot => slot.state == 'disabled').map(slot => slot.slot_number)
                }
                faultySlotNumbers={
                  kitchenConfiguration.plate_stands
                    ?.filter(slot => slot.is_faulty).map(slot => slot.slot_number)
                }
                activeReplenishmentOrders={activeReplenishmentOrders}
              />
              <OilView
                oilLevel={oilLevel}
                replenishmentView={replenishmentView}
                draftReplenishmentOrder={draftReplenishmentOrder}
                isSelectedTab={currentTabIndex == 3}
                expandOilContainer={expandOilContainer}
                activeReplenishmentOrders={activeReplenishmentOrders}
                weightScale={localization_context.weight_scale}
              />
            </SwipeableViews>
          </div>
        </div>
        {(expandedContainerSlot && !replenishmentView) &&
          <ProductContainerModal
            expandedContainerSlot={expandedContainerSlot}
            expandedContainer={
              (!!expandedContainerSlot.isLiquid ? liquidContainers : solidContainers)[expandedContainerSlot.number]
            }
            closeContainer={closeContainer}
            refreshStock={expandedContainerSlot.isLiquid ? refreshLiquidsStock : refreshSolidsStock}
          />
        }
        {(expandedContainerSlot && replenishmentView) &&
          <ProductReplenishmentModal
            containerSlot={expandedContainerSlot}
            selectedContainer={
              (!!expandedContainerSlot.isLiquid ? liquidContainers : solidContainers)[expandedContainerSlot.number]
            }
            closeModal={closeContainer}
            draftReplenishmentOrder={draftReplenishmentOrder}
            activeReplenishmentOrders={activeReplenishmentOrders}
            updateCart={updateReplenishmentCart}
            toggleSidebar={toggleReplenishmentSidebar}
          />
        }
        {(expandedPlateContainerSlot && !replenishmentView) &&
          <PlateContainerModal
            expandedContainerSlot={expandedPlateContainerSlot}
            expandedContainer={plateContainers[expandedPlateContainerSlot]}
            closeContainer={closePlateContainer}
            refreshStock={refreshPlatesStock}
          />
        }
        {(expandedPlateContainerSlot && replenishmentView) &&
          <BowlReplenishmentModal
            containerSlot={expandedPlateContainerSlot}
            container={plateContainers[expandedPlateContainerSlot]}
            closeModal={closePlateContainer}
            updateCart={updateReplenishmentCart}
            draftReplenishmentOrder={draftReplenishmentOrder}
            toggleSidebar={toggleReplenishmentSidebar}
          />
        }
        {(oilContainerExpanded && replenishmentView) &&
         <OilReplenishmentModal
            oilLevel={oilLevel}
            closeModal={() => setOilContainerExpanded(false)}
            draftReplenishmentOrder={draftReplenishmentOrder}
            updateCart={updateReplenishmentCart}
            toggleSidebar={toggleReplenishmentSidebar}
            weightScale={localization_context.weight_scale}
          />
        }
        {renderFlashNotification()}
        <ReplenishmentCartSidebar
          draftReplenishmentOrder={draftReplenishmentOrder}
          sidebarOpen={replenishmentSidebarOpen}
          toggleSidebar={toggleReplenishmentSidebar}
          updateCart={updateReplenishmentCart}
          errorMessages={replenishmentErrorMessages}
          orderState={orderState}
          setFlashMessage={setFlashMessage}
          onCartRefresh={fetchDraftReplenishmentOrder}
        />
        <ReplenishmentHistorySidebar
          sidebarOpen={replenishmentHistorySidebarOpen}
          closeSidebar={() => setReplenishmentHistorySidebarOpen(false)}
          activeOrders={activeReplenishmentOrders}
          fetchActiveOrders={fetchActiveReplenishmentOrders}
          setFlashMessage={setFlashMessage}
        />
        {showOrderPlacedModal.show &&
          <ReplenishmentOrderPlacedModal
            showOrderPlacedModal={showOrderPlacedModal}
            replenishmentOrder={placedReplenishmentOrder}
            handleClose={() => {
              setShowOrderPlacedModal({ show: false, orderState: '' })
              setPlacedReplenishmentOrder(null)
            }}
            location={current_location}
          />
        }
      </LocalizationContext.Provider>
    </div>
  )
}

export default StockControl
